import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import apiClient from "../apiClient";
import { toast } from "sonner";

export type TriggerType = "INVOICE_DATE" | "DUE_DATE" | "OVERDUE_RECURRING";
export type ConditionBalance = "GT_0" | "ANY";
export type TemplateAudienceType = "SINGLE_INVOICE" | "MULTIPLE_INVOICES";
export type InvoiceInclusionScope = "ALL_OPEN" | "OVERDUE_OPEN" | "OVERDUE_SINCE";
export type EmailSendStatus = "sent" | "failed" | "skipped";

export interface TemplateGroupSummary {
  id: number;
  name: string;
}

export interface EmailTemplate {
  id: number;
  budy_organization_id: number;
  name: string;
  subject: string;
  body: string;
  trigger_type: TriggerType;
  offset_days: number;
  condition_balance: ConditionBalance;
  audience_type: TemplateAudienceType;
  invoice_list_format?: string | null;
  enabled: boolean;
  repeat_interval_days?: number | null;
  invoice_inclusion_scope?: InvoiceInclusionScope | null;
  inclusion_min_days_overdue?: number | null;
  last_run_at?: string | null;
  created_at: string;
  updated_at: string;
  group_ids: number[];
  groups: TemplateGroupSummary[];
}

export interface EmailSend {
  id: number;
  template_id: number;
  payment_id: number;
  sent_at: string;
  status: EmailSendStatus;
  error_message?: string | null;
  recipient_emails?: string[] | null;
  invoice_external_ids?: string[] | null;
  gmail_thread_id?: string | null;
  gmail_message_id?: string | null;
  payment_external_id?: string | null;
}

export interface CreateEmailTemplateData {
  name: string;
  subject: string;
  body: string;
  trigger_type: TriggerType;
  offset_days: number;
  condition_balance: ConditionBalance;
  audience_type?: TemplateAudienceType;
  invoice_list_format?: string | null;
  enabled?: boolean;
  group_ids?: number[];
  repeat_interval_days?: number | null;
  invoice_inclusion_scope?: InvoiceInclusionScope | null;
  inclusion_min_days_overdue?: number | null;
}

export type TemplateVariableScope = "always" | "single" | "multi" | "list_item";

export interface TemplateVariable {
  key: string;
  label: string;
  description: string;
  example: string;
  scope: TemplateVariableScope;
  category: string;
}

export interface UpdateEmailTemplateData {
  name?: string;
  subject?: string;
  body?: string;
  trigger_type?: TriggerType;
  offset_days?: number;
  condition_balance?: ConditionBalance;
  audience_type?: TemplateAudienceType;
  invoice_list_format?: string | null;
  enabled?: boolean;
  group_ids?: number[];
  repeat_interval_days?: number | null;
  invoice_inclusion_scope?: InvoiceInclusionScope | null;
  inclusion_min_days_overdue?: number | null;
}

export interface SendNowEmailTemplateResult {
  status: string;
  job_id: string;
  message?: string | null;
}

export interface SendNowEmailTemplateStatus {
  job_id: string;
  status: "pending" | "running" | "completed" | "failed" | string;
  stats?: {
    templates_processed?: number;
    emails_sent?: number;
    emails_failed?: number;
    customers_evaluated?: number;
    customers_skipped_chatting?: number;
  } | null;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
}

export interface SendNowPreviewInvoice {
  external_id: string;
  amount: number;
  currency: string;
  due_date: string;
  days_overdue: number;
  already_sent: boolean;
}

export interface SendNowPreviewEmailBatch {
  subject: string;
  body_html: string;
  invoice_external_ids: string[];
}

export interface SendNowPreviewCustomer {
  customer_organization_id: number;
  customer_name: string;
  company_name?: string | null;
  recipient_emails: string[];
  status: "will_send" | "skipped" | string;
  skip_reason?: string | null;
  invoices: SendNowPreviewInvoice[];
  email_batch_count: number;
  email_batches: SendNowPreviewEmailBatch[];
}

export interface SendNowPreviewCustomerDraft {
  customer_organization_id: number;
  customer_name: string;
  company_name?: string | null;
  recipient_emails: string[];
  status: "will_send" | "skipped" | string;
  skip_reason?: string | null;
  email_batch_count: number;
  email_batches: SendNowPreviewEmailBatch[];
}

export interface SendNowPreviewSummary {
  total_customers: number;
  customers_will_send: number;
  customers_skipped: number;
  total_invoices: number;
  invoices_will_send: number;
  emails_to_send: number;
}

export interface SendNowPreviewResponse {
  template_id: number;
  template_name: string;
  template_enabled: boolean;
  summary: SendNowPreviewSummary;
  customers: SendNowPreviewCustomer[];
  total: number;
  skip: number;
  limit: number;
}

export const SEND_NOW_PREVIEW_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
export const SEND_NOW_PREVIEW_DEFAULT_PAGE_SIZE = 25;

export const emailTemplateKeys = {
  all: ["email_templates"] as const,
  variables: () => [...emailTemplateKeys.all, "variables"] as const,
  lists: () => [...emailTemplateKeys.all, "list"] as const,
  list: (filters: string) =>
    [...emailTemplateKeys.lists(), { filters }] as const,
  details: () => [...emailTemplateKeys.all, "detail"] as const,
  detail: (id: number) => [...emailTemplateKeys.details(), id] as const,
  sends: (id: number) => [...emailTemplateKeys.detail(id), "sends"] as const,
  sendNowStatus: (id: number, jobId: string) =>
    [...emailTemplateKeys.detail(id), "send_now", jobId] as const,
  sendNowPreview: (id: number, skip: number, limit: number) =>
    [...emailTemplateKeys.detail(id), "send_now", "preview", { skip, limit }] as const,
  sendNowCustomerDraft: (templateId: number, customerId: number) =>
    [
      ...emailTemplateKeys.detail(templateId),
      "send_now",
      "preview",
      "customer",
      customerId,
    ] as const,
};

export const useEmailTemplateVariablesQuery = (
  options?: Omit<UseQueryOptions<TemplateVariable[]>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: emailTemplateKeys.variables(),
    queryFn: async () => {
      const { data } = await apiClient.get<TemplateVariable[]>(
        "/email_templates/variables",
      );
      return data;
    },
    staleTime: 1000 * 60 * 10,
    ...options,
  });

export const useEmailTemplatesQuery = (
  options?: Omit<UseQueryOptions<EmailTemplate[]>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: emailTemplateKeys.lists(),
    queryFn: async () => {
      const { data } =
        await apiClient.get<EmailTemplate[]>("/email_templates/");
      return data;
    },
    ...options,
  });

export const useEmailTemplateQuery = (
  templateId: number,
  options?: Omit<UseQueryOptions<EmailTemplate>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: emailTemplateKeys.detail(templateId),
    queryFn: async () => {
      const { data } = await apiClient.get<EmailTemplate>(
        `/email_templates/${templateId}`,
      );
      return data;
    },
    enabled: !!templateId,
    ...options,
  });

export const useEmailTemplateSendsQuery = (
  templateId: number,
  options?: Omit<UseQueryOptions<EmailSend[]>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: emailTemplateKeys.sends(templateId),
    queryFn: async () => {
      const { data } = await apiClient.get<EmailSend[]>(
        `/email_templates/${templateId}/sends`,
      );
      return data;
    },
    enabled: !!templateId,
    ...options,
  });

export const useCreateEmailTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateData: CreateEmailTemplateData) => {
      const { data } = await apiClient.post<EmailTemplate>(
        "/email_templates/",
        templateData,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.lists() });
      toast.success("Email template created successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.detail?.message ||
          "Failed to create email template",
      );
    },
  });
};

export const useUpdateEmailTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      templateData,
    }: {
      templateId: number;
      templateData: UpdateEmailTemplateData;
    }) => {
      const { data } = await apiClient.put<EmailTemplate>(
        `/email_templates/${templateId}`,
        templateData,
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.detail(data.id),
      });
      toast.success("Email template updated successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.detail?.message ||
          "Failed to update email template",
      );
    },
  });
};

export const useDeleteEmailTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: number) => {
      await apiClient.delete(`/email_templates/${templateId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.lists() });
      toast.success("Email template deleted successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.detail?.message ||
          "Failed to delete email template",
      );
    },
  });
};

export const useEmailTemplateSendNowPreviewQuery = (
  templateId: number,
  options?: {
    skip?: number;
    limit?: number;
    queryOptions?: Omit<
      UseQueryOptions<SendNowPreviewResponse>,
      "queryKey" | "queryFn"
    >;
  },
) => {
  const skip = options?.skip ?? 0;
  const limit = options?.limit ?? SEND_NOW_PREVIEW_DEFAULT_PAGE_SIZE;

  return useQuery({
    queryKey: emailTemplateKeys.sendNowPreview(templateId, skip, limit),
    queryFn: async () => {
      const { data } = await apiClient.get<SendNowPreviewResponse>(
        `/email_templates/${templateId}/send_now/preview`,
        { params: { skip, limit } },
      );
      return data;
    },
    enabled: !!templateId,
    staleTime: 0,
    placeholderData: (previousData) => previousData,
    ...options?.queryOptions,
  });
};

export const useEmailTemplateSendNowCustomerDraftQuery = (
  templateId: number,
  customerOrganizationId: number | null,
  options?: Omit<
    UseQueryOptions<SendNowPreviewCustomerDraft>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: emailTemplateKeys.sendNowCustomerDraft(
      templateId,
      customerOrganizationId ?? 0,
    ),
    queryFn: async () => {
      const { data } = await apiClient.get<SendNowPreviewCustomerDraft>(
        `/email_templates/${templateId}/send_now/preview/customers/${customerOrganizationId}`,
      );
      return data;
    },
    enabled: !!templateId && !!customerOrganizationId,
    staleTime: 1000 * 60 * 5,
    ...options,
  });

export const useSendEmailTemplateNowMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: number) => {
      const { data } = await apiClient.post<SendNowEmailTemplateResult>(
        `/email_templates/${templateId}/send_now`,
      );
      return { templateId, ...data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.lists() });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.detail?.message ||
          "Failed to start template send",
      );
    },
  });
};

const TERMINAL_SEND_STATUSES = new Set(["completed", "failed"]);

export const useEmailTemplateSendNowStatusQuery = (
  templateId: number,
  jobId: string | null,
  options?: Omit<
    UseQueryOptions<SendNowEmailTemplateStatus>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: emailTemplateKeys.sendNowStatus(templateId, jobId ?? ""),
    queryFn: async () => {
      const { data } = await apiClient.get<SendNowEmailTemplateStatus>(
        `/email_templates/${templateId}/send_now/status`,
        { params: { job_id: jobId } },
      );
      return data;
    },
    enabled: !!templateId && !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && TERMINAL_SEND_STATUSES.has(status)) {
        return false;
      }
      return 2000;
    },
    ...options,
  });
